import { Grid, Typography } from '@mui/material';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import React, { useEffect, useState } from 'react';
import { ApplicationProps, Document } from '../../redux-store/types/api-types';
import { Notes, ProceedSaveLater } from './components';
import { documentDetails, FileUpload } from './documentDetails';
import { useDispatch, useSelector } from 'react-redux';
import { getDocuments, updateApplication } from '../../redux-store/actions/application';
import { APPLICATION_TYPE_FOR_DOCUMENTS, docInvalidCheck } from '../../utils/constant';
import {
  applicationComparison,
  checkIfApplicationIsNonIndividual,
} from '../../utils/utilityFunctions';
import { RootStateType } from '../../redux-store/reducers';
import { phygitaldDocumentDetailsSchema } from '../../utils/schema';
import { showError } from '../../redux-store/actions/auth';
import { useHistory } from 'react-router';

export type documentProps = {
  documentType: string;
  documentName: string;
  documentsList: documentDetails[];
  multipleFiles: string;
  required: string;
  options?: string[];
  infoText?: string[];
  downloadTemplate?: string;
  errorOnUpload?: boolean;
};

export type Values = {
  phygitalDocuments: documentProps[];
  saveType: string;
  [key: string]: documentProps[] | string | number | undefined | string[] | boolean | null;
};

export default function PhygitalDocumentDetails(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const { application } = useSelector((store: RootStateType) => store.application);

  const { signedLeegalityFile } = application || {};
  const [documentsData, setDocumentsData] = useState<Document>();
  const [loading, setLoading] = useState(false);
  const [documentUpload, setDocumentUpload] = useState(false);

  const handleUpload = () => setDocumentUpload(true);
  const handleUploaded = () => setDocumentUpload(false);
  useEffect(() => {
    (async function () {
      try {
        const response = (await dispatch(getDocuments())) as unknown as Document;
        setDocumentsData(response as Document);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, []);
  const docData = APPLICATION_TYPE_FOR_DOCUMENTS.PHYGITAL;

  const documents =
    typeof documentsData !== 'undefined'
      ? (((documentsData as Document)[docData] || []) as any)
      : [];

  const initialValues: Values = {
    phygitalDocuments: documents
      ?.filter((doc: documentProps) => doc?.documentType === 'phygitalDocument')
      ?.map((doc: documentProps) => {
        const baseDocument = {
          documentType: doc?.documentType,
          documentName: doc?.documentName,
          documentId: signedLeegalityFile?.id || null,
          multipleFiles: doc?.multipleFiles,
          required: doc?.required,
          options: doc?.options,
          infoText: doc?.infoText,
          downloadTemplate: doc?.downloadTemplate,
          errorOnUpload: false,
          file: signedLeegalityFile?.id ? signedLeegalityFile : undefined,
        };

        return {
          ...baseDocument,
          documentsList: [{ ...baseDocument, documentsList: doc?.documentsList || [] }],
        };
      }),
    saveType: 'save and proceed',
  };
  // console.log(values, 'values');
  const handleSubmit = async (values: Values) => {
    try {
      setLoading(true);
      const { id, signedLeegalityFile, signedLeegalityDocumentId } = application || {};
      const phygitalDocument = values?.phygitalDocuments?.find(
        (_doc) => _doc?.documentType === 'phygitalDocument'
      )?.documentsList?.[0];

      if (phygitalDocument?.errorOnUpload) {
        throw docInvalidCheck;
      }

      const legalityId = phygitalDocument?.documentId;

      const documentIsChanges =
        parseInt(signedLeegalityDocumentId || '') !== parseInt(legalityId || '');

      const checkApplication = applicationComparison(
        {
          ...application,
        },
        {
          ...application,
          signedLeegalityDocumentId: legalityId,
        }
      );
      if (documentIsChanges && id && !checkApplication) {
        await dispatch(
          updateApplication({
            body: {
              ...application,
              signedLeegalityDocumentId: legalityId,
            },
            applicationId: id,
          })
        );
      }
      checkIfApplicationIsNonIndividual(application as ApplicationProps)
        ? history.push(`/non-individual-application-details/${id}`)
        : history.push(`/application-preview/${id}`);
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e));
      console.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={(values: Values) => {
        try {
          validateYupSchema(values, phygitaldDocumentDetailsSchema(), true, values);
        } catch (e) {
          return yupToFormErrors(e);
        }
      }}
      enableReinitialize>
      {({ handleSubmit, values, setFieldValue }) => (
        <Grid
          container
          rowSpacing={1}
          sx={{
            width: '100%',
            ml: 0,
            '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
          }}
          component="form"
          noValidate
          onSubmit={handleSubmit}>
          <Grid item xs={12} sx={{ width: '100%' }}>
            <Notes
              displayContent="Please upload the latest documents not more than 2 months old."
              displayContent1="Please don't upload any password-protected files."
            />
          </Grid>
          {values.phygitalDocuments.map((document, doc_index) => {
            const {
              documentType,
              documentName,
              documentsList = [],
              multipleFiles,
              required,
            } = document;

            return (
              <React.Fragment key={doc_index}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    component="h3"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                      mt: 6,
                    }}>
                    {documentName} {required === 'true' ? '*' : ''}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  {documentsList.map((doc, index) => (
                    <React.Fragment key={doc.uniqueKey}>
                      <FileUpload
                        isPhygitalDoc={true}
                        onFileChange={(value, documentId, errorOnUpload, removeFile) => {
                          const documentPath = `phygitalDocuments.${doc_index}.documentsList.${index}`;
                          const { file, ...rest } =
                            values.phygitalDocuments[doc_index].documentsList[index];
                          setFieldValue(documentPath, {
                            ...rest,
                            documentId,
                            errorOnUpload: errorOnUpload || false,
                            ...(!removeFile && { file }),
                          });
                        }}
                        name={`phygitalDocuments.${doc_index}.documentsList.${index}.documentId`}
                        documentData={doc}
                        disabled={false}
                        onFileUpload={handleUpload}
                        onFileUploaded={handleUploaded}
                        displayReplaceButton={true}
                        multipleFiles={multipleFiles}
                        index={index}
                        documentsLength={documentsList.length}
                      />
                    </React.Fragment>
                  ))}
                </Grid>
              </React.Fragment>
            );
          })}

          <ProceedSaveLater
            saveButtonText={'Preview'}
            showSaveForLater={false}
            saveAndProceed={() => setFieldValue('saveType', 'save and proceed')}
            clickedButton={values.saveType}
            disabled={documentUpload}
            loader={loading}
          />
        </Grid>
      )}
    </Formik>
  );
}
